import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtRM } from "../../lib/format";
import { useLang } from "../../lib/i18n";
import { PageTitle, Spinner } from "../../components/Shell";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const COLUMNS = [
  { key: "new", label: "NEW", stages: ["NEW LEAD", "CONTACTED"], color: "border-slate-300" },
  { key: "demo", label: "DEMO", stages: ["DEMO BOOKED", "DEMO COMPLETED"], color: "border-blue-300" },
  { key: "proposal", label: "PROPOSAL", stages: ["PROPOSAL", "NEGOTIATION"], color: "border-gold" },
  { key: "won", label: "WON / LIVE", stages: ["WON", "ONBOARDING", "LIVE", "RENEWAL"], color: "border-brand-vivid" },
  { key: "lost", label: "LOST", stages: ["LOST"], color: "border-red-300" },
];

const ALL_STAGES = ["NEW LEAD", "CONTACTED", "DEMO BOOKED", "DEMO COMPLETED", "PROPOSAL", "NEGOTIATION", "WON", "LOST", "ONBOARDING", "LIVE", "RENEWAL"];

export default function Crm() {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ coop_name: "", contact_person: "", email: "", phone: "", member_size: "", current_system: "", main_problem: "", plan: "professional", est_value: 0, next_action: "", probability: 10, notes: "" });
  const { lang } = useLang();

  const load = () => api.get("/platform/leads").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const moveStage = async (lead, stage) => {
    try {
      await api.put(`/platform/leads/${lead.lead_id}`, { stage });
      load();
    } catch {
      toast.error("Error");
    }
  };

  const createLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/leads", { ...form, est_value: Number(form.est_value) || 0, probability: Number(form.probability) || 0 });
      toast.success("Lead ditambah");
      setShowForm(false);
      setForm({ coop_name: "", contact_person: "", email: "", phone: "", member_size: "", current_system: "", main_problem: "", plan: "professional", est_value: 0, next_action: "", probability: 10, notes: "" });
      load();
    } catch {
      toast.error("Error");
    }
  };

  if (!data) return <Spinner />;

  return (
    <div data-testid="crm-pipeline-board">
      <PageTitle title={lang === "bm" ? "Saluran Jualan" : "Sales Pipeline"} sub={`Pipeline: ${fmtRM(data.pipeline_value)} • Won: ${fmtRM(data.won_value)}`}>
        <button data-testid="add-lead-btn" onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 bg-brand-deep text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-brand-dark transition-colors">
          <Plus className="w-4 h-4" /> Lead
        </button>
      </PageTitle>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {COLUMNS.map((col) => {
          const leads = data.leads.filter((l) => col.stages.includes(l.stage));
          return (
            <div key={col.key} className={`bg-brand-light/40 rounded-xl border-t-4 ${col.color} p-3 min-h-[200px]`} data-testid={`crm-column-${col.key}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-dark">{col.label}</span>
                <span className="text-xs font-bold text-muted-foreground bg-white rounded-full px-2 py-0.5">{leads.length}</span>
              </div>
              <div className="space-y-3">
                {leads.map((l) => (
                  <div key={l.lead_id} className="bg-card rounded-lg border border-brand-deep/10 p-3.5 hover:shadow-md transition-shadow" data-testid={`lead-card-${l.lead_id.slice(-6)}`}>
                    <div className="font-semibold text-sm text-brand-dark leading-snug">{l.coop_name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{l.contact_person} • {l.member_size || "-"}</div>
                    {l.est_value > 0 && <div className="font-mono text-xs font-semibold text-brand-deep mt-1.5">{fmtRM(l.est_value)}/thn</div>}
                    {l.next_action && <div className="text-[11px] text-gold mt-1.5 font-medium">→ {l.next_action}</div>}
                    <select
                      data-testid={`lead-stage-${l.lead_id.slice(-6)}`}
                      value={l.stage}
                      onChange={(e) => moveStage(l, e.target.value)}
                      className="mt-2.5 w-full text-[11px] rounded-md border border-input bg-white px-2 py-1.5 focus:outline-none"
                    >
                      {ALL_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <form onSubmit={createLead} className="relative bg-card rounded-2xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto animate-fade-up" data-testid="lead-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-brand-dark">Lead Baharu</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-brand-dark"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="lead-coop-input" required placeholder="Nama Koperasi *" value={form.coop_name} onChange={(e) => setForm({ ...form, coop_name: e.target.value })} className="sm:col-span-2 rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input data-testid="lead-person-input" placeholder="Pegawai" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input data-testid="lead-email-input" placeholder="Emel" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input placeholder="Saiz Ahli" value={form.member_size} onChange={(e) => setForm({ ...form, member_size: e.target.value })} className="rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input placeholder="Sistem Semasa" value={form.current_system} onChange={(e) => setForm({ ...form, current_system: e.target.value })} className="rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input data-testid="lead-value-input" type="number" placeholder="Anggaran Nilai (RM/thn)" value={form.est_value} onChange={(e) => setForm({ ...form, est_value: e.target.value })} className="rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <textarea placeholder="Masalah Utama" value={form.main_problem} onChange={(e) => setForm({ ...form, main_problem: e.target.value })} className="sm:col-span-2 rounded-lg border border-input px-3.5 py-2.5 text-sm" rows={2} />
              <input placeholder="Tindakan Seterusnya" value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className="sm:col-span-2 rounded-lg border border-input px-3.5 py-2.5 text-sm" />
            </div>
            <button data-testid="lead-submit-btn" type="submit" className="mt-4 w-full bg-brand-deep text-white rounded-xl py-2.5 text-sm font-bold hover:bg-brand-dark transition-colors">Simpan Lead</button>
          </form>
        </div>
      )}
    </div>
  );
}
